import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface Election {
  id: string;
  title: string;
  status: string;
  voting_method: string;
  opens_at: number;
  closes_at: number;
  quorum_pct: number;
  passing_threshold: string;
  created_at: number;
}

export function Elections() {
  const qc = useQueryClient();
  const list = useQuery<{ elections: Election[] }>({ queryKey: ['elections'], queryFn: () => api.get('/elections') });
  const [creating, setCreating] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Elections</h1>
        <button onClick={() => setCreating(true)} className="px-3 py-1.5 bg-brand text-white rounded text-sm hover:bg-brand-600">New election</button>
      </div>

      {list.isLoading && <p className="text-slate-500">Loading...</p>}
      {list.data && (
        <div className="bg-white border border-slate-200 rounded">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Opens</th>
                <th className="px-3 py-2">Closes</th>
                <th className="px-3 py-2">Quorum</th>
              </tr>
            </thead>
            <tbody>
              {list.data.elections.map((e) => (
                <tr key={e.id} className="border-b last:border-b-0">
                  <td className="px-3 py-2"><Link to={`/app/elections/${e.id}`} className="text-brand hover:underline font-medium">{e.title}</Link></td>
                  <td className="px-3 py-2"><StatusBadge status={e.status} /></td>
                  <td className="px-3 py-2 text-slate-600">{new Date(e.opens_at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-slate-600">{new Date(e.closes_at).toLocaleString()}</td>
                  <td className="px-3 py-2">{e.quorum_pct}%</td>
                </tr>
              ))}
              {list.data.elections.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">No elections yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateElectionModal onClose={() => setCreating(false)} onCreated={() => { qc.invalidateQueries({ queryKey: ['elections'] }); setCreating(false); }} />}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const c = status === 'open' ? 'bg-green-100 text-green-700' : status === 'closed' ? 'bg-slate-200 text-slate-700' : status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
  return <span className={`px-2 py-0.5 rounded text-xs ${c}`}>{status}</span>;
}

function CreateElectionModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const opensDefault = new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16);
  const closesDefault = new Date(Date.now() + 8 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16);

  const [form, setForm] = useState({
    title: '',
    description: '',
    proposedActionText: '',
    opensAt: opensDefault,
    closesAt: closesDefault,
    quorumPct: 20,
    passingThreshold: 'majority',
    customPctValue: '',
    isEmergency: false,
    governingDocsReference: '',
    eligibleRoles: ['owner'] as string[],
  });
  type Q = { questionText: string; questionType: 'yes_no_abstain' | 'single_choice' | 'multi_choice' | 'ranked_choice'; options: string[]; minSelections: number; maxSelections: number };
  const [questions, setQuestions] = useState<Q[]>([
    { questionText: 'Approve the proposed action?', questionType: 'yes_no_abstain', options: ['Yes', 'No', 'Abstain'], minSelections: 1, maxSelections: 1 },
  ]);

  const create = useMutation({
    mutationFn: (body: unknown) => api.post('/elections', body),
    onSuccess: onCreated,
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const body = {
      title: form.title,
      description: form.description || undefined,
      proposedActionText: form.proposedActionText,
      opensAt: new Date(form.opensAt).toISOString(),
      closesAt: new Date(form.closesAt).toISOString(),
      quorumPct: form.quorumPct,
      passingThreshold: form.passingThreshold,
      customPctValue: form.passingThreshold === 'custom_pct' ? Number(form.customPctValue) : undefined,
      isEmergency: form.isEmergency,
      governingDocsReference: form.governingDocsReference || undefined,
      eligibleRoles: form.eligibleRoles,
      votingMethod: 'without_meeting',
      questions: questions.map((q, i) => ({
        position: i,
        questionText: q.questionText,
        questionType: q.questionType,
        minSelections: q.minSelections,
        maxSelections: q.maxSelections,
        options: q.options.filter((o) => o.trim()).map((text, pos) => ({ position: pos, optionText: text })),
      })),
    };
    create.mutate(body);
  }

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-10 p-4 overflow-auto">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 my-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg">Create election</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>

        <div className="space-y-3">
          <Field label="Title"><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-slate-300 rounded px-3 py-2" /></Field>
          <Field label="Description (optional)"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full border border-slate-300 rounded px-3 py-2" /></Field>
          <Field label="Proposed action (verbatim — included in notice email per LA RS 9:1141.28(G))">
            <textarea required value={form.proposedActionText} onChange={(e) => setForm({ ...form, proposedActionText: e.target.value })} rows={4} className="w-full border border-slate-300 rounded px-3 py-2" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Opens"><input type="datetime-local" required value={form.opensAt} onChange={(e) => setForm({ ...form, opensAt: e.target.value })} className="w-full border border-slate-300 rounded px-3 py-2" /></Field>
            <Field label="Closes (≥7 days after opens)"><input type="datetime-local" required value={form.closesAt} onChange={(e) => setForm({ ...form, closesAt: e.target.value })} className="w-full border border-slate-300 rounded px-3 py-2" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quorum %"><input type="number" min={1} max={100} required value={form.quorumPct} onChange={(e) => setForm({ ...form, quorumPct: Number(e.target.value) })} className="w-full border border-slate-300 rounded px-3 py-2" /></Field>
            <Field label="Passing threshold">
              <select value={form.passingThreshold} onChange={(e) => setForm({ ...form, passingThreshold: e.target.value })} className="w-full border border-slate-300 rounded px-3 py-2">
                <option value="majority">Majority</option>
                <option value="two_thirds">Two-thirds</option>
                <option value="three_fourths">Three-fourths</option>
                <option value="custom_pct">Custom %</option>
              </select>
            </Field>
          </div>
          {form.passingThreshold === 'custom_pct' && (
            <Field label="Custom % required"><input type="number" min={1} max={100} value={form.customPctValue} onChange={(e) => setForm({ ...form, customPctValue: e.target.value })} className="w-full border border-slate-300 rounded px-3 py-2" /></Field>
          )}
          <Field label="Governing-docs reference (only required if closes < 7 days after opens)">
            <input value={form.governingDocsReference} onChange={(e) => setForm({ ...form, governingDocsReference: e.target.value })}
              placeholder="e.g. Bylaws Article V §3" className="w-full border border-slate-300 rounded px-3 py-2" />
          </Field>

          <div className="border-t pt-3 mt-2">
            <h3 className="font-semibold text-sm mb-2">Ballot questions</h3>
            {questions.map((q, qi) => (
              <div key={qi} className="border border-slate-200 rounded p-3 mb-2">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-medium text-slate-500">Question {qi + 1}</span>
                  {questions.length > 1 && <button type="button" onClick={() => setQuestions(questions.filter((_, i) => i !== qi))} className="text-red-600 text-xs">Remove</button>}
                </div>
                <input value={q.questionText} onChange={(e) => { const n = [...questions]; n[qi] = { ...n[qi]!, questionText: e.target.value }; setQuestions(n); }}
                  placeholder="Question text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm mb-2" />
                <select value={q.questionType} onChange={(e) => {
                  const n = [...questions];
                  const t = e.target.value as Q['questionType'];
                  const defaultOpts = t === 'yes_no_abstain' ? ['Yes', 'No', 'Abstain'] : n[qi]!.options;
                  n[qi] = { ...n[qi]!, questionType: t, options: defaultOpts, minSelections: 1, maxSelections: t === 'multi_choice' ? 2 : 1 };
                  setQuestions(n);
                }} className="w-full border border-slate-300 rounded px-2 py-1 text-sm mb-2">
                  <option value="yes_no_abstain">Yes / No / Abstain</option>
                  <option value="single_choice">Single choice</option>
                  <option value="multi_choice">Multi-choice (pick N)</option>
                  <option value="ranked_choice">Ranked choice (IRV)</option>
                </select>
                {q.questionType === 'multi_choice' && (
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <label className="text-xs">Min selections<input type="number" min={1} value={q.minSelections} onChange={(e) => { const n = [...questions]; n[qi] = { ...n[qi]!, minSelections: Number(e.target.value) }; setQuestions(n); }} className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></label>
                    <label className="text-xs">Max selections<input type="number" min={1} value={q.maxSelections} onChange={(e) => { const n = [...questions]; n[qi] = { ...n[qi]!, maxSelections: Number(e.target.value) }; setQuestions(n); }} className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></label>
                  </div>
                )}
                {q.questionType !== 'yes_no_abstain' && (
                  <div className="space-y-1">
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex gap-2">
                        <input value={opt} onChange={(e) => { const n = [...questions]; const o = [...n[qi]!.options]; o[oi] = e.target.value; n[qi] = { ...n[qi]!, options: o }; setQuestions(n); }}
                          placeholder={`Option ${oi + 1}`} className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm" />
                        {q.options.length > 2 && <button type="button" onClick={() => { const n = [...questions]; n[qi] = { ...n[qi]!, options: n[qi]!.options.filter((_, i) => i !== oi) }; setQuestions(n); }} className="text-red-600 text-xs px-2">×</button>}
                      </div>
                    ))}
                    <button type="button" onClick={() => { const n = [...questions]; n[qi] = { ...n[qi]!, options: [...n[qi]!.options, ''] }; setQuestions(n); }} className="text-brand text-xs">+ add option</button>
                  </div>
                )}
              </div>
            ))}
            <button type="button" onClick={() => setQuestions([...questions, { questionText: '', questionType: 'yes_no_abstain', options: ['Yes', 'No', 'Abstain'], minSelections: 1, maxSelections: 1 }])} className="text-brand text-sm">+ add question</button>
          </div>

          {create.error && <div className="text-sm text-red-600">{create.error.message}</div>}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 border border-slate-300 rounded text-sm">Cancel</button>
          <button type="submit" disabled={create.isPending} className="px-4 py-2 bg-brand text-white rounded font-medium disabled:opacity-50">{create.isPending ? 'Creating...' : 'Save as draft'}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="text-sm font-medium block mb-1">{label}</span>{children}</label>;
}
